/**
 * Audit Query and Statistics Generation
 *
 * Functionality for querying audit events and generating statistics
 */

import { AuditEvent, AuditQuery, AuditStats } from './types';
import { getCurrentISOTimestamp, getTimestampHoursAgo } from '@/utils/date';

/**
 * Query stored audit events with filtering and pagination
 */
export function queryEvents(
  events: AuditEvent[],
  anonymizeData: boolean,
  hashUserId: (_userId?: string) => string | undefined,
  query: AuditQuery = {}
): AuditEvent[] {
  let filteredEvents = [...events];

  if (query.userId) {
    const targetUserId = anonymizeData ? hashUserId(query.userId) : query.userId;
    filteredEvents = filteredEvents.filter(event => event.userId === targetUserId);
  }

  // SessionId filtering - check if sessionId exists in query
  if ('sessionId' in query && query.sessionId) {
    filteredEvents = filteredEvents.filter(event => event.sessionId === query.sessionId);
  }

  if (query.action) {
    filteredEvents = filteredEvents.filter(event => event.action === query.action);
  }

  if (query.resource) {
    filteredEvents = filteredEvents.filter(event => event.resource === query.resource);
  }

  if (query.result) {
    filteredEvents = filteredEvents.filter(event => event.result === query.result);
  }

  // Level filtering - using 'level' instead of 'securityLevel'
  if (query.level) {
    filteredEvents = filteredEvents.filter(event => event.level === query.level);
  }

  if (query.startDate) {
    const startTime = new Date(query.startDate).getTime();
    filteredEvents = filteredEvents.filter(event =>
      new Date(event.timestamp).getTime() >= startTime
    );
  }

  if (query.endDate) {
    const endTime = new Date(query.endDate).getTime();
    filteredEvents = filteredEvents.filter(event =>
      new Date(event.timestamp).getTime() <= endTime
    );
  }

  // Apply pagination
  const offset = query.offset || 0;
  const limit = query.limit || filteredEvents.length;

  return filteredEvents
    .sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime())
    .slice(offset, offset + limit);
}

/**
 * Generate comprehensive audit statistics
 */
export function generateStats(
  events: AuditEvent[],
  timeRangeHours: number = 24
): AuditStats {
  const cutoffTime = getTimestampHoursAgo(timeRangeHours);
  const recentEvents = events.filter(event =>
    new Date(event.timestamp).getTime() >= cutoffTime
  );

  const resourceCounts = new Map<string, number>();
  const uniqueUsers = new Set<string>();
  let _deniedAttempts = 0;
  let _securityViolations = 0;

  recentEvents.forEach(event => {
    if (event.userId) {
      uniqueUsers.add(event.userId);
    }

    if (event.result === 'DENIED') {
      _deniedAttempts++;
    }

    if (event.level === 'ERROR' || event.level === 'CRITICAL') {
      _securityViolations++;
    }

    if (event.resource) {
      resourceCounts.set(event.resource, (resourceCounts.get(event.resource) || 0) + 1);
    }
  });

  const topResources = Array.from(resourceCounts.entries())
    .map(([resource, count]) => ({ resource, accessCount: count }))
    .sort((a, b) => b.accessCount - a.accessCount)
    .slice(0, 10);

  return {
    totalEvents: recentEvents.length,
    eventsByLevel: recentEvents.reduce((acc, event) => {
      acc[event.level] = (acc[event.level] || 0) + 1;
      return acc;
    }, {} as Record<string, number>),
    eventsByAction: recentEvents.reduce((acc, event) => {
      acc[event.action] = (acc[event.action] || 0) + 1;
      return acc;
    }, {} as Record<string, number>),
    eventsByResult: recentEvents.reduce((acc, event) => {
      acc[event.result] = (acc[event.result] || 0) + 1;
      return acc;
    }, {} as Record<string, number>),
    recentEvents: recentEvents.slice(0, 100),
    timeRange: {
      startDate: new Date(cutoffTime).toISOString(),
      endDate: getCurrentISOTimestamp()
    },
    topUsers: Array.from(uniqueUsers).slice(0, 10).map(userId => ({
      userId,
      eventCount: recentEvents.filter(e => e.userId === userId).length
    })),
    topResources
  };
}

/**
 * Export audit logs for compliance in various formats
 */
export function exportForCompliance(
  _events: AuditEvent[],
  auditLevel: string,
  anonymizeData: boolean,
  queryFn: (_query: AuditQuery) => AuditEvent[],
  convertToCSV: (_events: AuditEvent[]) => string,
  startDate: Date,
  endDate: Date,
  format: 'JSON' | 'CSV' = 'JSON'
): string {
  const filteredEvents = queryFn({ startDate: startDate.toISOString(), endDate: endDate.toISOString() });

  if (format === 'CSV') {
    return convertToCSV(filteredEvents);
  }

  return JSON.stringify({
    exportTimestamp: getCurrentISOTimestamp(),
    auditLevel,
    anonymized: anonymizeData,
    timeRange: {
      start: startDate.toISOString(),
      end: endDate.toISOString()
    },
    totalEvents: filteredEvents.length,
    events: filteredEvents
  }, null, 2);
}

/**
 * Convert audit events to CSV format
 */
export function convertToCSV(events: AuditEvent[]): string {
  if (events.length === 0) return '';

  const headers = [
    'id', 'timestamp', 'level', 'action', 'userId', 'userEmail', 'resource',
    'permission', 'result', 'sessionId', 'ipAddress', 'userAgent', 'companyId', 'environment'
  ];

  const rows = events.map(event =>
    headers.map(header => {
      const value = event[header as keyof AuditEvent];
      return value ? `"${value.toString().replace(/"/g, '""')}"` : '';
    }).join(',')
  );

  return [headers.join(','), ...rows].join('\n');
}